import * as React from 'react';
import { DragSource, DropTarget } from 'react-dnd';
import { connect } from 'react-redux';
import { bindMethods } from '../../utils/core';
import { onPageContainerDrop, setActivePageContainer } from '../../actions/reduxActions/pageEditor';
import { CONTAINER_TYPE, PAGE_ELEMENTS } from './pageEditorConstants';
import classnames from 'classnames';
import Modal from '../generic/Modal';
import '../../../css/page/container-editor.less';

const CONTAINER_BOUNDS_OFFSET = 25; // Pixels

const CONTAINER_EDGES = {
    top: 'top',
    bottom: 'bottom',
    left: 'left',
    right: 'right',
    all: 'all',
};

const mapStateToProps = ({ pageEditor }) => ({ pageEditor });

const mapDispatchToProps = {
    onPageContainerDrop,
    setActivePageContainer,
};

const containerSource = {

    // These are the props from the container being dragged/dropped
    // that we want to be exposed during those events
    beginDrag(props) {
        return {
            id: props.id,
            order: props.order,
            parentId: props.parentId,
        };
    },
};

/**
 * @param props the component props of the container being hovered over
 * @param hoverClientX number of pixels from the left edge of the container
 * @param hoverMiddleX horizontal middle of container
 * @param hoverClientY number of pixels from the top edge of the container
 * @param hoverMiddleY vertical middle of container
 * @param component node of the container being hovered over
 * @description determine which edge of the container being hovered over gets highlighted
 */
export const setContainerBorder =
(
    props,
    hoverClientX,
    hoverMiddleX,
    hoverClientY,
    hoverMiddleY,
    component,
) => {

    let edge = null;

    const offsetBottom = hoverMiddleY + CONTAINER_BOUNDS_OFFSET;
    const offsetTop = hoverMiddleY - CONTAINER_BOUNDS_OFFSET;

    // If the container has a parent we need to know it's container
    // type to determine whether to highlight the vertical or horizontal edges
    const currentContainersParent = props.parentId ? props.pageEditor.pageContainers.find(
        container => container.id === props.parentId,
    ) : component.props;

    if // Mouse is hovering over to the right
    (
        hoverClientX > hoverMiddleX &&
        currentContainersParent.containerType === CONTAINER_TYPE.column
    ) {
        edge = CONTAINER_EDGES.right;
    }

    if // Mouse is hovering over to the left
    (
        hoverClientX < hoverMiddleX &&
        currentContainersParent.containerType === CONTAINER_TYPE.column
    ) {
        edge = CONTAINER_EDGES.left;
    }

    if // Mouse is hovering towards the top
    (
        hoverClientY < hoverMiddleY &&
        currentContainersParent.containerType === CONTAINER_TYPE.row
    ) {
        edge = CONTAINER_EDGES.top;
    }

    if // Mouse is hovering towards the bottom
    (
        hoverClientY > hoverMiddleY &&
        currentContainersParent.containerType === CONTAINER_TYPE.row
    ) {
        edge = CONTAINER_EDGES.bottom;
    }

    // Mouse is hovering in the center
    if (
        (
            currentContainersParent.containerType === CONTAINER_TYPE.column &&
            hoverClientX > CONTAINER_BOUNDS_OFFSET &&
            hoverClientX < (hoverMiddleX * 2) - CONTAINER_BOUNDS_OFFSET
        )
            ||
        (
            hoverClientY < offsetBottom &&
            hoverClientY > offsetTop &&
            currentContainersParent.containerType === CONTAINER_TYPE.row
        )
    ) {
        edge = CONTAINER_EDGES.all;
    }

    component.setState(
        { edge },
    );
};

export const containerTarget = {

    /**
     * @param props the props of the container being dropped onto
     * @param monitor utility methods for accessing container node being dropped
     * @param component node of the container being dropped onto
     */
    drop(props, monitor, component) {
        const hasDroppedOnChild = monitor.didDrop();

        // We are only interested in the drop event
        // for the immediate container, not it's ancestors
        if (hasDroppedOnChild) {
            return undefined;
        }

        const {
            type: containerDroppedType,
            id: containerDroppedId,
            order: containerDroppedOrder,
            parentId: containerDroppedParentId,
            component: containerDroppedComponent,
        } = monitor.getItem();

        const containerEdge = component.state.edge;

        // We only want to re-order if the container is not being dropped on itself
        if
        (containerDroppedId !== props.id &&
        props.parentId !== containerDroppedId) {

            const containerType = (
                containerDroppedType || containerDroppedComponent
            ) === PAGE_ELEMENTS.row_container ?
                CONTAINER_TYPE.row :
                CONTAINER_TYPE.column;

            // If there is no container edge defined then
            // we want to nest the container within it
            if (containerEdge === CONTAINER_EDGES.all) {

                const { pageContainers } = props.pageEditor;

                // This is just so that if the container is dropped
                // into the main container, it will always be ordered last
                const order = pageContainers.filter(
                    container => container.parentId === props.id,
                ).length;

                props.onPageContainerDrop(
                    containerDroppedId || null,
                    props.id,
                    order,
                    containerType,
                );

            } else {

                // Re-ordering containers upwards
                if (containerEdge === CONTAINER_EDGES.top || containerEdge === CONTAINER_EDGES.left) {
                    if
                    (containerDroppedOrder !== props.order - 1 ||
                    containerDroppedParentId !== props.parentId) {
                        props.onPageContainerDrop(
                            containerDroppedId || null,
                            props.parentId,
                            (
                                containerDroppedOrder < props.order &&
                                containerDroppedParentId === props.parentId
                            ) ? props.order - 1 : props.order,
                            containerType,
                        );
                    }
                }

                // Re-ordering containers downwards
                if (containerEdge === CONTAINER_EDGES.bottom || containerEdge === CONTAINER_EDGES.right) {
                    props.onPageContainerDrop(
                        containerDroppedId || null,
                        props.parentId,
                        (
                            containerDroppedOrder < props.order &&
                            containerDroppedParentId === props.parentId
                        ) ? props.order : props.order + 1,
                        containerType,
                    );
                }
            }
        }

        // The container ID needs to be available for
        // when the sidebar component hooks into the drop result
        return {
            id: props.id,
        };
    },

    /**
     * @param props the props of the container being hovered over
     * @param monitor utility methods for accessing container node being dragged
     * @param component node of the container being hovered over
     */
    hover(props, monitor, component) {
        if (!component) {
            return null;
        }

        const dragIndex = monitor.getItem().order;
        const hoverIndex = props.order;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex && monitor.getItem().parentId === props.parentId) {
            return null;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = component.decoratedComponentInstance.node.getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Get horizontal middle
        const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY = (clientOffset).y - hoverBoundingRect.top;

        // Get pixels to the left
        const hoverClientX = clientOffset.x - hoverBoundingRect.left;

        if (props.parentId) {
            setContainerBorder(
                props,
                hoverClientX,
                hoverMiddleX,
                hoverClientY,
                hoverMiddleY,
                component,
            );
        } else {
            component.setState(
                { edge: CONTAINER_EDGES.all },
            );
        }

        return null;
    },
};

class PageContainer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            configOpen: false,
            edge: null,
        };

        bindMethods(
            this,
            'openConfig',
            'closeConfig',
            'focusContainer',
        );
    }

    componentDidMount() {
        if (this.props.isActive) {
            this.node.focus();
        }
    }

    focusContainer(e) {
        e.stopPropagation();
        this.props.setActivePageContainer(this.props.id);
    }

    openConfig() {
        this.setState({ configOpen: true });
    }

    closeConfig() {
        this.setState({ configOpen: false });
    }

    render() {
        const {
            isDragging,
            connectDragSource,
            connectDropTarget,
            isOverCurrent,
            connectDragPreview,
            edge,
            type,
            children,
            parentId,
        } = this.props;

        const opacity = isDragging ? 0 : 1;

        const containerClasses = classnames({
            'page-container': true,
            'edge-top': edge === CONTAINER_EDGES.top && isOverCurrent,
            'edge-bottom': edge === CONTAINER_EDGES.bottom && isOverCurrent,
            'edge-left': edge === CONTAINER_EDGES.left && isOverCurrent,
            'edge-right': edge === CONTAINER_EDGES.right && isOverCurrent,
            'edge-all': edge === CONTAINER_EDGES.all && isOverCurrent,
        });

        const bodyClasses = classnames({
            'page-container-body': true,
            'page-container-row': type === PAGE_ELEMENTS.row_container,
            'page-container-column': type === PAGE_ELEMENTS.column_container,
        });

        const containerMarkup = (
            connectDragPreview(
                <div
                    className={containerClasses}
                    ref={(node => this.node = node)} // Bug in React DnD preventing use of createRef
                    tabIndex="0"
                    onClick={this.focusContainer}
                    style={{ opacity }}
                >
                    {
                        parentId ? (
                            <div className="page-container-header draggable">
                                {connectDragSource(
                                    <div className="drag-handle" />,
                                )}
                                <div className="container-type">{type}</div>
                                <button className="btn-icon glyphicon glyphicon-cog page-container-config" onClick={this.openConfig} />
                            </div>
                        ) : null
                    }
                    <div className={bodyClasses}>
                        {
                            children
                        }
                    </div>
                    <Modal
                        show={this.state.configOpen}
                        title="Container Settings"
                        renderBody={() => (
                            <div>
                                <label>
                                    <span className="label-above">Name</span>
                                    <input className="textbox" defaultValue="" />
                                </label>
                            </div>
                        )}
                        renderFooter={() => (
                            <div className="flex">
                                <div className="flex-child-right">
                                    <button className="btn btn-sm btn-secondary" onClick={this.closeConfig}>Cancel</button>
                                    <button className="btn btn-sm btn-success" onClick={this.closeConfig}>Update</button>
                                </div>
                            </div>
                        )}
                    />
                </div>,
            )
        );

        if (parentId) {
            return connectDropTarget(
                containerMarkup,
            );
        }
        return connectDropTarget(containerMarkup);
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(
    DropTarget(
        ['container', 'SideBarComponent'],
        containerTarget,
        (connectDnD, monitor) => ({
            connectDropTarget: connectDnD.dropTarget(),
            isOverCurrent: monitor.isOver({ shallow: true }),
            isOver: monitor.isOver(),
        }),
    )(
        DragSource(
            'container',
            containerSource,
            (connectDnD, monitor) => ({
                connectDragSource: connectDnD.dragSource(),
                isDragging: monitor.isDragging(),
                connectDragPreview: connectDnD.dragPreview(),
            }),
        )(PageContainer),
    ),
);
